import {useCallback, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {getSavedListsCounts} from 'src/openapi/admin/apis/UserContentCountApi';
import {useAppDispatch} from 'src/store/hooks';

import {makeSelectListsByUserId, selectListsReceivedAt} from '../slices/listsSelectors';
import {UserPageState, invalidateUserLists} from '../slices/listsSlice';

type Props = {
    scopeKey?: string,
    userId: number,
    autoload?: boolean,
    ttlMs?: number,
};

export const useUserListsCount = ({userId, autoload = true, ttlMs = 120_000}: Props) => {
    const dispatch = useDispatch();
    const apiDispatch = useAppDispatch();

    const selectLists = useMemo(
        () => makeSelectListsByUserId(userId),
        [userId],
    );

    const {total, status, error} = useSelector(selectLists);

    const refetch = useCallback(() => {
        apiDispatch(getSavedListsCounts({userId}));
    }, [apiDispatch, userId]);

    const invalidate = useCallback(() => {
        dispatch(invalidateUserLists(userId));
    }, [dispatch, userId]);

    const receivedAt = useSelector((state: UserPageState) => selectListsReceivedAt(state, userId));

    useEffect(() => {
        if (!autoload) {
            return;
        }

        if (status === 'idle' || status === 'failed') {
            refetch();
            return;
        }

        if (status === 'succeeded' && receivedAt) {
            const now = Date.now();
            if (now - receivedAt > ttlMs) {
                refetch();
            }
        }
    }, [autoload, status, receivedAt, ttlMs, refetch]);

    return {
        total,
        status,
        error,
        refetch,
        invalidate,
        isFetching: status === 'loading',
    };
};
